import { Pipe, PipeTransform } from '@angular/core';

/**
* Takes a string and an array of strings, returns an HTML string with the array of strings highlighted in the original string
*/
@Pipe({
    name: 'highlight',
    standalone: false
})
export class HighlightPipe implements PipeTransform {

    private cache: {[key: string]: RegExp};
    private readonly specialCharactersPattern: RegExp = /[\-\[\]\/\{\}\(\)\+\.\\\^\$\|]/g;

    constructor() {
        this.cache = {};
    }

    /**
    * Wraps search strings found in text in wrapper provided by
    *
    * @param text             - Text to be searched and replaced inside
    * @param search           - Keywords to search for and replace in text
    * @param hightlightClass  - (OPTIONAL) The class to apply in the highlight
    * @param wrapper          - (OPTIONAL) The wrapper to wrap the found keywords in (replaced keyword identified by {{0}})
    *
    * @returns string
    */
    transform(
        text: string,
        search: string[],
        hightlightClass = 'agpr-highlight',
        wrapper?: string,
        caseSensitive?: boolean,
        matchWithinWord?: boolean
    ): string {
        if (!text || !search || search.length === 0) {
            return text;
        }

        if (!wrapper) {
            wrapper = `<span class="${hightlightClass}">{{0}}</span>`;
        }

        const indexKey = search.join('|') + '-' + !!caseSensitive;
        let regex = this.cache[indexKey];

        if (!regex) {
            const searchTermsMap = search.map(searchTerm => {
                searchTerm = searchTerm.replace(this.specialCharactersPattern, '\\$&');

                // Replace wildcard character "*" with regex that will match the rest of the word.
                searchTerm = searchTerm.replace(/\*/g, '\\w*');

                // Replace wildcard character "?" with regex that will match 1 character.
                searchTerm = searchTerm.replace(/\?/g, '.');

                // Replace either apostrophe characters ' or ’ with regex that will match either of them.
                searchTerm = searchTerm.replace(/['’]/g, '[\'’]');

                return searchTerm;
            });

            let expFlag = 'gi';
            if (caseSensitive) {
                expFlag = 'g';
            }

            const searchTerms = searchTermsMap.join('|');

            // detect non ASCII characters
            const asciiRegex = new RegExp('^[ -~]+$');
            if (!asciiRegex.test(searchTerms) || matchWithinWord) {
                // don't use /b word boundaries because it doesn't work well with non ASCII characters
                regex = new RegExp('<|>|(' + searchTerms + ')', expFlag);
            } else {
                regex = new RegExp('<|>|\\b(' + searchTerms + ')\\b', expFlag);
            }
            this.cache[indexKey] = regex;
        }

        let level = 0;

        return text.replace(
            regex,
            (match) => {
                // Don't match inside of html tags
                if (match === '<') {
                    level++;

                    return match;
                } else if (match === '>') {
                    level--;

                    return match;
                }

                if (level > 0) {
                    return match;
                }

                return wrapper.replace('{{0}}', match);
            }
        );
    }
}
